import { motion } from "framer-motion";
import { Dna, Leaf, Flame } from "lucide-react";

const CHAPTERS = [
    {
        n: "01",
        title: "Ethical Herpetology",
        icon: Dna,
        body: "Every animal is captive bred in our own facility — never wild caught. Documented lineage, established feeders, and honest temperament notes with every specimen.",
    },
    {
        n: "02",
        title: "Naturalistic Keeping",
        icon: Leaf,
        body: "Every animal in our care lives in a planted, enriched environment that mirrors its wild habitat — because well-kept animals become healthy, confident animals for your home.",
    },
    {
        n: "03",
        title: "Uncompromising Wilderness",
        icon: Flame,
        body: "From cerulean island vipers to emerald canopy pythons, we keep only what we can keep perfectly. Live arrival guaranteed, climate-controlled overnight freight, lifetime keeper support.",
    },
];

export const Manifesto = () => (
    <section id="manifesto" data-testid="manifesto-section" className="relative py-24 lg:py-32">
        <div
            className="pointer-events-none absolute inset-0 opacity-[0.1]"
            style={{
                backgroundImage:
                    "url(https://images.unsplash.com/photo-1696482156853-8ee190d9cfda?crop=entropy&cs=srgb&fm=jpg&q=85)",
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-12">
            <motion.p
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                className="font-mono2 text-xs uppercase tracking-[0.35em] text-red-500"
            >
                The Manifesto
            </motion.p>
            <motion.h2
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="mt-4 max-w-2xl font-display text-2xl font-extrabold tracking-tight text-bone sm:text-3xl lg:text-4xl"
            >
                Three laws govern everything that leaves our facility.
            </motion.h2>

            <div className="mt-16 grid grid-cols-1 gap-8 border-t border-red-950/60 pt-12 md:grid-cols-3">
                {CHAPTERS.map((c, i) => (
                    <motion.article
                        key={c.n}
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.7, delay: i * 0.12 }}
                        data-testid={`manifesto-chapter-${c.n}`}
                        className="spotlight-card group relative border border-red-950/50 bg-coal/70 p-8 backdrop-blur-sm transition-colors duration-500 hover:border-red-800/70"
                    >
                        <span className="font-mono2 text-xs tracking-[0.3em] text-red-500">
                            {c.n} /
                        </span>
                        <c.icon className="mt-6 h-7 w-7 text-red-600 transition-colors duration-500 group-hover:text-red-400" />
                        <h3 className="mt-4 font-display text-lg font-bold text-bone sm:text-xl">
                            {c.title}
                        </h3>
                        <p className="mt-4 text-sm leading-relaxed text-ash">{c.body}</p>
                    </motion.article>
                ))}
            </div>
        </div>
    </section>
);
